import { z } from 'zod';

import { currencyCodeSchema } from '~/lib/atom/types/currency';
import { FlashSaleSchema } from '~/lib/atom/types/flash-sale';

const priceSchema = z.object({
  v: z.number(),
  e: z.number(),
});

const priceMapSchema = z.record(
  z.string().regex(/^\d+$/).transform(Number),
  z.object({
    p: priceSchema,
    s: priceSchema.optional(),
  }),
);

const stringToJSONSchema = z.union([z.string(), FlashSaleSchema]).transform((str, ctx): unknown => {
  try {
    if (typeof str === 'string') {
      const json: unknown = JSON.parse(str);

      return json;
    } else if (typeof str === 'object') {
      return str;
    }

    throw new Error('Invalid type');
  } catch {
    ctx.addIssue({ code: 'custom', message: 'Invalid JSON' });

    return z.NEVER;
  }
});

export const hitSchema = z.object({
  objectID: z.string(),
  image_url: z.string(),
  url: z.string(),
  sku: z.string(),
  name: z.string(),
  release_date: z.number().nullish(),
  in_stock: z.boolean(),
  is_best_seller: z.boolean().nullish(),
  has_buy_button: z.boolean().nullish(),
  age: z.number().nullish(),
  abv: z.number().nullish(),
  volume: z.number().nullish(),
  options: z
    .object({
      Option: z.string().nullish(),
    })
    .nullish(),
  description: z.string().nullish(),
  category_ids: z.array(z.number()).nullish(),
  atom_prices: z.record(currencyCodeSchema, priceMapSchema).nullish(),
  atom_has_bespoke_price: z.record(z.string(), z.boolean()).nullish(),
  atom_average_rating: z.number().nullish(),
  atom_rating_count: z.number().nullish(),
  product_metafields: z
    .object({
      'atom.product': z.object({
        country: z.string().nullish(),
        age: z.string().nullish(),
        'short-name': z.string().nullish(),
        bottler: z.string().nullish(),
        distillery: z.string().nullish(),
        category: z.string().nullish(),
        style: z.string().nullish(),
      }),
    })
    .optional(),
  variant_metafields: z
    .object({
      'atom.product': z.object({
        volume: z.string().nullish(),
        'has-retail-price-drop': z.coerce.number().nullish(),
        'is-staff-pick': z.coerce.number().nullish(),
        'has-free-delivery': z.coerce.number().nullish(),
        'flash-sale': stringToJSONSchema.pipe(FlashSaleSchema).nullish(),
        'is-preorder': z.coerce.number().nullish(),
      }),
    })
    .optional(),
});
export type Hit = z.infer<typeof hitSchema>;
